/**
 * Client-side downscaling before upload.
 *
 * Ten photos straight off a phone is 40 MB and minutes on a mobile network.
 * Resizing in the browser first turns that into a few hundred kilobytes each,
 * which is the whole difference between "under a minute" and "give up". The
 * server still re-encodes what arrives — this is for speed, not for trust.
 */

const MAX_EDGE = 1600
const QUALITY = 0.82

/**
 * Engines disagree wildly about what a given JPEG quality costs — WebKit
 * produces roughly twice the bytes Chromium does at 0.82 — so the target is a
 * size, not a quality number. Without this, an upload that is comfortable on
 * Android goes over a minute on an iPhone.
 */
const TARGET_BYTES = 600 * 1024
const RETRY_QUALITIES = [0.72, 0.62]

/** Reads a file into an ImageBitmap, falling back to an <img> where needed. */
async function decode(file) {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file)
    } catch {
      // Safari refuses some HEIC variants here; fall through to the element.
    }
  }

  const url = URL.createObjectURL(file)

  try {
    const image = new Image()
    image.src = url
    await image.decode()

    return image
  } finally {
    URL.revokeObjectURL(url)
  }
}

/**
 * Returns a JPEG File no larger than MAX_EDGE on its longest side.
 * The original is returned untouched if it is already small enough.
 */
export async function downscale(file, { maxEdge = MAX_EDGE, quality = QUALITY } = {}) {
  if (!file.type.startsWith('image/')) {
    return file
  }

  let source

  try {
    source = await decode(file)
  } catch {
    // Unreadable here is not fatal: let the server decide.
    return file
  }

  const width = source.width ?? source.naturalWidth
  const height = source.height ?? source.naturalHeight
  const scale = Math.min(1, maxEdge / Math.max(width, height))

  if (scale === 1 && file.size < 1_500_000) {
    return file
  }

  const canvas = document.createElement('canvas')
  canvas.width = Math.round(width * scale)
  canvas.height = Math.round(height * scale)

  const context = canvas.getContext('2d')
  context.drawImage(source, 0, 0, canvas.width, canvas.height)

  const encode = (q) =>
    new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', q)
    })

  let blob = await encode(quality)

  for (const lower of RETRY_QUALITIES) {
    if (blob === null || blob.size <= TARGET_BYTES) {
      break
    }

    blob = await encode(lower)
  }

  if (blob === null) {
    return file
  }

  const name = file.name.replace(/\.[^.]+$/, '') + '.jpg'

  return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified })
}

/** Downscales a whole batch, reporting progress as each one finishes. */
export async function downscaleAll(files, onProgress) {
  const out = []

  for (const [index, file] of [...files].entries()) {
    out.push(await downscale(file))
    onProgress?.(index + 1, files.length)
  }

  return out
}
